/**
 * Asset Configuration — the two reference tables that everything else in the Asset module hangs
 * off: Categories (e.g. "IT Equipment") and, nested under a category, Types (e.g. "13-inch
 * laptop"). Vendors (`vendor.contract.ts`) reference a category too, but live in their own
 * contract since they're a distinct admin page, not a child of this one.
 *
 * `typeCount` is a real derived count (how many AssetType rows point at this category).
 * `assetCount` on both DTOs is a stub that always reads 0 — there is no Asset inventory table yet
 * (out of scope for this slice; see the plan). It's shaped into the DTO now so the future Asset
 * inventory feature is additive to the response, not a breaking-change migration for every
 * consumer of these schemas.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { assetCsvExportSchema } from './vendor.contract.js';
import { routeMeta } from './meta.js';
import {
  assetCustodySignoffSchema,
  assetDepreciationMethodSchema,
  errorBodySchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';

const c = initContract();

/** Blank optional text arrives from forms as ''; store it as null/omitted, not as an empty value. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const assetCategoryDetailSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  defaultDepreciationMethod: assetDepreciationMethodSchema,
  defaultDepreciationPeriodYears: z.number().int().nullable(),
  custodySignoff: assetCustodySignoffSchema,
  isActive: z.boolean(),
  /** Count of AssetType rows in this category — real, derived server-side. */
  typeCount: z.number().int().nonnegative(),
  /**
   * STUB: always 0. There is no Asset inventory table yet — this field is reserved for a future
   * "assets currently assigned to this category" count once that table exists.
   */
  assetCount: z.number().int().nonnegative(),
});
export type AssetCategoryDetail = z.infer<typeof assetCategoryDetailSchema>;

export const assetTypeDetailSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  categoryId: uuidSchema,
  /** Denormalized for display — so a list of types needs no per-row category lookup. */
  categoryName: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  /** STUB: always 0. See assetCategoryDetailSchema.assetCount — same reasoning, same future table. */
  assetCount: z.number().int().nonnegative(),
});
export type AssetTypeDetail = z.infer<typeof assetTypeDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listAssetCategoriesQuerySchema = paginationQuerySchema.extend({
  /** Row ids are uuids; a malformed cursor is a 422 here, not a Prisma error surfacing as a 500. */
  cursor: uuidSchema.optional(),
  /** Matches code or name. */
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('all'),
});

// Named with the "Configuration" qualifier (not the bare `createAssetCategoryBodySchema` you might
// expect): `asset.contract.ts` (the asset register's own contract) already exports a same-named
// `createAssetCategoryBodySchema`/`CreateAssetCategoryBody` for its own, thinner create-category
// shape. Both are legitimate — this one is the Configuration admin screen's superset (description,
// depreciation, custody sign-off) — but `contracts/index.ts`'s `export *` re-exports both files, so
// an unqualified name here would be a TS2308 ambiguous-export error at the barrel.
export const createAssetConfigurationCategoryBodySchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  description: optionalText(2000),
  defaultDepreciationMethod: assetDepreciationMethodSchema.optional(),
  defaultDepreciationPeriodYears: z.number().int().min(0).max(100).optional(),
  custodySignoff: assetCustodySignoffSchema.optional(),
});

/** Every field optional; code is immutable once created, matching updateDepartmentBodySchema. */
export const updateAssetCategoryBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  defaultDepreciationMethod: assetDepreciationMethodSchema.optional(),
  defaultDepreciationPeriodYears: z.number().int().min(0).max(100).nullable().optional(),
  custodySignoff: assetCustodySignoffSchema.optional(),
  isActive: z.boolean().optional(),
});

export const listAssetTypesQuerySchema = paginationQuerySchema.extend({
  /** Row ids are uuids; a malformed cursor is a 422 here, not a Prisma error surfacing as a 500. */
  cursor: uuidSchema.optional(),
  /** Matches code or name. */
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('all'),
  categoryId: uuidSchema.optional(),
});

// Same qualification, same reason: asset.contract.ts already exports
// `createAssetTypeBodySchema`/`CreateAssetTypeBody`.
export const createAssetConfigurationTypeBodySchema = z.object({
  categoryId: uuidSchema,
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  description: optionalText(2000),
});

/**
 * Every field optional; `categoryId` is settable (re-categorizing a type is a normal admin
 * action), unlike `code`, which is immutable once created.
 */
export const updateAssetTypeBodySchema = z.object({
  categoryId: uuidSchema.optional(),
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  isActive: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const assetConfigurationContract = c.router(
  {
    listAssetCategories: {
      method: 'GET',
      path: '/categories',
      query: listAssetCategoriesQuerySchema,
      responses: {
        200: paginatedSchema(assetCategoryDetailSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List asset categories',
      metadata: routeMeta('asset.category:read'),
    },
    createAssetCategory: {
      method: 'POST',
      path: '/categories',
      body: createAssetConfigurationCategoryBodySchema,
      responses: {
        201: assetCategoryDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create an asset category',
      metadata: routeMeta('asset.category:manage'),
    },
    updateAssetCategory: {
      method: 'PATCH',
      path: '/categories/:categoryId',
      body: updateAssetCategoryBodySchema,
      responses: {
        200: assetCategoryDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update an asset category',
      metadata: routeMeta('asset.category:manage'),
    },

    exportAssetConfiguration: {
      method: 'GET',
      path: '/export',
      query: z.object({ type: z.enum(['categories', 'types']) }),
      responses: {
        200: assetCsvExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Export asset categories or types as CSV (asset:export)',
      metadata: routeMeta('asset:export'),
    },
    listAssetTypes: {
      method: 'GET',
      path: '/types',
      query: listAssetTypesQuerySchema,
      responses: {
        200: paginatedSchema(assetTypeDetailSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List asset types, optionally filtered by category',
      metadata: routeMeta('asset.type:read'),
    },
    createAssetType: {
      method: 'POST',
      path: '/types',
      body: createAssetConfigurationTypeBodySchema,
      responses: {
        201: assetTypeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create an asset type under a category',
      metadata: routeMeta('asset.type:manage'),
    },
    updateAssetType: {
      method: 'PATCH',
      path: '/types/:typeId',
      body: updateAssetTypeBodySchema,
      responses: {
        200: assetTypeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update an asset type',
      metadata: routeMeta('asset.type:manage'),
    },
  },
  { pathPrefix: '/asset-management' },
);
